import styled from 'styled-components';
import { FlexColumn, FlexRow } from '@make-software/cspr-design';
import { BodyText, Tooltip } from '@block-explorer/ui';
import { AccountInfoIcon } from '../../account-info-icon/account-info-icon';
import { ACCOUNT_DETAILS_PATH, SEARCH_PATH } from '../../../router/paths';
import React from 'react';
import { SHORTENED_ENTRY_TYPE_LENGTH, truncateName } from '../utils';
import {
  AccountInfoResult,
  Deploy,
  FTEntryPointType,
} from '@block-explorer/entities';
import { useAccountInfoContext } from '../../../services/account-data-service';
import { buildRouterPath } from '../../../router/utils';
import { DefaultActionMessage } from './default-action-message';
import { ftTokenMessage } from '../../../services/deploy-action-messages';
import { deriveAccountInfo } from '../../../utils/account';
import { ContractIdentifier } from '../../contract-identifier/contract-identifier';
import { isWASMProxyTransaction } from '../../../utils/contract';
import { WasmProxyBadge } from '../wasm-proxy-badge';
import { useTranslation } from 'react-i18next';
import { X402Badge } from '../x402-badge';

const EntryTypeRow = styled(FlexRow)(() => ({
  maxWidth: '25rem',
}));

const StyledBodyText = styled(BodyText)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: 13,
  lineHeight: '13px',
}));

export const FtTokenActionMessage = ({ deploy }: { deploy: Deploy }) => {
  const { t } = useTranslation();
  const { getPublicKeyAccountHash, getAccountInfoByHash, getAccountInfo } =
    useAccountInfoContext();
  const message = ftTokenMessage().prepareActionMessageData(
    deploy,
    getPublicKeyAccountHash,
    t
  );

  if (!message) {
    return <DefaultActionMessage deploy={deploy} />;
  }

  const account1Hash = message.account1?.accountHash;
  const account1PublicKey = message.account1?.publicKey;

  const publicKey1WithFallbackToHash = account1PublicKey || account1Hash;
  const fromApiAccountInfo =
    getAccountInfo<AccountInfoResult>(account1PublicKey || '') ||
    getAccountInfoByHash(account1Hash);

  const fromApiAccountInfoDetails = deriveAccountInfo(
    fromApiAccountInfo?.account_info ||
      fromApiAccountInfo?.centralized_account_info
  );

  const path1 =
    (publicKey1WithFallbackToHash &&
      buildRouterPath(
        account1PublicKey ? ACCOUNT_DETAILS_PATH : SEARCH_PATH,
        { id: publicKey1WithFallbackToHash },
        undefined
      )) ||
    '';

  const account2ContractHash = message.account2?.hash;
  const account2Hash = message.account2?.accountHash;
  const account2PublicKey = message.account2?.publicKey;

  const publicKey2WithFallbackToHash = account2PublicKey || account2Hash;
  const toApiAccountInfo =
    getAccountInfo<AccountInfoResult>(account2PublicKey || '') ||
    getAccountInfoByHash(account2Hash);

  const toApiAccountInfoDetails = deriveAccountInfo(
    toApiAccountInfo?.account_info || toApiAccountInfo?.centralized_account_info
  );
  const path2 =
    (publicKey2WithFallbackToHash &&
      buildRouterPath(
        account2PublicKey ? ACCOUNT_DETAILS_PATH : SEARCH_PATH,
        { id: publicKey2WithFallbackToHash },
        undefined
      )) ||
    '';

  const isX402Transaction =
    message.entryPointName === FTEntryPointType.transfer_with_authorization;

  return (
    <FlexColumn>
      <EntryTypeRow align={'center'} itemsSpacing={4}>
        {isX402Transaction && <X402Badge />}
        {isWASMProxyTransaction(deploy.executionTypeId) && <WasmProxyBadge />}
        <FlexRow itemsSpacing={6} align={'center'}>
          <Tooltip tooltipContent={message.entryPointName}>
            <BodyText monotype size={3} wordBreak noWrap lineHeight={'sm'}>
              {truncateName(message.actionName, SHORTENED_ENTRY_TYPE_LENGTH)}
            </BodyText>
          </Tooltip>
          {message.symbol && (
            <BodyText monotype size={3} wordBreak noWrap lineHeight={'sm'}>
              {message.symbol}
            </BodyText>
          )}

          {message.account1 && publicKey1WithFallbackToHash && (
            <FlexRow align="center" itemsSpacing={6}>
              {message.prefix1 && (
                <StyledBodyText size={3} noWrap variation={'darkGray'}>
                  {message.prefix1}
                </StyledBodyText>
              )}
              <AccountInfoIcon
                publicKey={publicKey1WithFallbackToHash}
                logo={
                  fromApiAccountInfoDetails && fromApiAccountInfoDetails?.logo
                }
                path={path1}
                csprName={fromApiAccountInfo?.cspr_name}
              />
            </FlexRow>
          )}
          {message.account2 && publicKey2WithFallbackToHash && (
            <FlexRow align="center" itemsSpacing={6}>
              {message.prefix2 && (
                <StyledBodyText noWrap size={3} variation={'darkGray'}>
                  {message.prefix2}
                </StyledBodyText>
              )}
              <AccountInfoIcon
                publicKey={publicKey2WithFallbackToHash}
                logo={toApiAccountInfoDetails && toApiAccountInfoDetails?.logo}
                path={path2}
                csprName={toApiAccountInfo?.cspr_name}
              />
            </FlexRow>
          )}
          {message.account2 && account2ContractHash && (
            <FlexRow align="center" itemsSpacing={6}>
              {message.prefix2 && (
                <StyledBodyText size={3} noWrap variation={'darkGray'}>
                  {message.prefix2}
                </StyledBodyText>
              )}
              <AccountInfoIcon
                publicKey={account2ContractHash}
                logo={null}
                path={buildRouterPath(
                  SEARCH_PATH,
                  { id: account2ContractHash },
                  undefined
                )}
              />
            </FlexRow>
          )}
        </FlexRow>
      </EntryTypeRow>
      <FlexRow itemsSpacing={4} align={'center'}>
        <StyledBodyText variation={'darkGray'} size={3}>
          with
        </StyledBodyText>
        <ContractIdentifier
          hash={deploy.contractPackage.contract_package_hash}
          contractPackage={deploy.contractPackage}
        />
      </FlexRow>
    </FlexColumn>
  );
};
